import React from "react"
import {Alert, Box, Button, CircularProgress, Typography} from "@mui/material"
import {useParams} from "react-router-dom"

import {channelPresenter} from "./channelPresenter"
import {CandidateList} from "./components/CandidateCard"
import {IdentifyPanel} from "./components/IdentifyPanel"
import {ItemHeader} from "./components/ItemHeader"
import {OutcomeCard} from "./components/OutcomeCard"
import {Thread} from "./components/Thread"
import {useUnidentifiedInbox} from "./useUnidentifiedInbox"

import {Layout} from "@indebted/components/Layout"
import {useAgent} from "@indebted/hooks/Agent"

// No back link: /inbox matches no route until the list page lands.
function PageFrame({children}: {children: React.ReactNode}) {
	return (
		<Layout>
			<Box px={3} pt={3} pb={4}>
				{children}
			</Box>
		</Layout>
	)
}

function ItemPage() {
	const {channel = "", conversationId = "", providerMessageId = ""} = useParams()
	const [agentEmail] = useAgent()
	const {data, loading, error, notFound, refetch} = useUnidentifiedInbox(
		channel,
		conversationId,
		providerMessageId,
		agentEmail,
	)

	if (loading) {
		return (
			<PageFrame>
				<Box display="flex" justifyContent="center" p={3}>
					<CircularProgress />
				</Box>
			</PageFrame>
		)
	}

	if (notFound) {
		return (
			<PageFrame>
				<Typography variant="h6" component="h1" mb={1}>
					Message not found
				</Typography>
				<Typography variant="body2" color="textSecondary">
					This message is not in the unidentified inbox. It may have been identified already, or the link may
					point at another region.
				</Typography>
			</PageFrame>
		)
	}

	if (error || !data) {
		return (
			<PageFrame>
				<Box display="flex" alignItems="center" gap={2}>
					<Typography color="error">{error || "Failed to load the message"}</Typography>
					<Button size="small" onClick={refetch}>
						Retry
					</Button>
				</Box>
			</PageFrame>
		)
	}

	if (!data.UnidentifiedInboxEnabled) {
		return (
			<PageFrame>
				<Alert severity="info">The unidentified inbox is not available for your account yet.</Alert>
			</PageFrame>
		)
	}

	const {Message: message, Thread: thread, Candidates: candidates} = data
	const presenter = channelPresenter(message.Channel)
	const isOpen = message.Status === "open"

	return (
		<PageFrame>
			<ItemHeader message={message} presenter={presenter} />
			<Box maxWidth={960}>
				<Thread messages={thread} threadSubject={message.Subject} senderAddress={message.From.Address} />
				<Box mt={4}>
					<IdentifyPanel
						candidates={isOpen ? <CandidateList candidates={candidates} /> : undefined}
						outcome={
							!isOpen && message.Resolution ? <OutcomeCard resolution={message.Resolution} /> : undefined
						}
					/>
				</Box>
			</Box>
		</PageFrame>
	)
}

export {ItemPage}
